import { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { CollapseGrabber } from "@/components/CollapseGrabber/CollapseGrabber";
import { Spinner } from "@/components/Spinner/Spinner";

import { ListSourceType } from "@/enum/list.enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { classNames } from "@/utils/classNames";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { useGetLeanListsQuery } from "@/pages/Private/redux/list/list.api";

import { useGetActiveGenerationsQuery } from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.api";
import {
	GenerationJob,
	selectGenerationJobs,
} from "@/pages/Private/redux/copywritingOrchestration/copywritingOrchestration.slice";
import {
	useGetFeedCompaniesQuery,
	useGetFeedCountsQuery,
	useGetFeedQuery,
} from "@/pages/Private/redux/feed/feed.api";
import {
	FEED_TRACKED_TAB,
	feedSelector,
	hydrateFeedFromUrl,
	jumpToFeedCompany,
	setFeedFilter,
	setFeedPage,
	setFeedPanelContextView,
	setFeedPriorityFilterStatus,
	setFeedSearch,
	setFeedSelectedCardId,
	setFeedSelectedLists,
	setFeedSignalFilterStatus,
	setFeedSort,
	setFeedTab,
} from "@/pages/Private/redux/feed/feed.slice";

import { ContactLeadPanel } from "@/pages/Private/pages/Contacts/components/ContactLeadPanel";

import { AiQueuePill } from "./components/AiQueuePill";
import { FeedEmptyState } from "./components/FeedEmptyState";
import { FeedFilterBar } from "./components/FeedFilterBar";
import { FeedPager } from "./components/FeedPager";
import { FeedTabItem, FeedTabs } from "./components/FeedTabs";
import { FeedTopbar } from "./components/FeedTopbar";
import { JumpToCompanyDropdown } from "./components/JumpToCompanyDropdown";
import { FeedUndoToast } from "./components/RunCard/FeedUndoToast";
import { RunCard } from "./components/RunCard/RunCard";
import { feedCardKey, feedIdentity } from "./utils/feedCardKey";
import { parseFeedListIdsParam, parseFeedPageParam } from "./utils/feedUrlParams";
import { isGenerationJobReady } from "./utils/isGenerationJobReady";
import { useCompanyGenerationsSocket } from "./utils/useCompanyGenerationsSocket";
import { useFeedDockedPanel } from "./utils/useFeedDockedPanel";
import { useFeedDraftsReadyToast } from "./utils/useFeedDraftsReadyToast";
import { useFeedJumpToCompany } from "./utils/useFeedJumpToCompany";
import { useFeedLeadStatus } from "./utils/useFeedLeadStatus";
import { FeedPendingAddsContext, useFeedPendingAdds } from "./utils/useFeedPendingAdds";
import { useFeedQueueJump } from "./utils/useFeedQueueJump";
import { useFeedReviewNow } from "./utils/useFeedReviewNow";
import { useFeedRunIgnore } from "./utils/useFeedRunIgnore";
import { useFeedTrackerRemoval } from "./utils/useFeedTrackerRemoval";

/** Upper bound for the "select all tracker lists" default on nav entry. */
const DEFAULT_TRACKER_LISTS_LIMIT = 100;

/**
 * Standalone Feed page shell (PREC-3635): topbar with the tracker-list
 * multi-select, live L2 tabs, L3 filter bar, run cards (P5), explicit
 * pager and the docked-panel layout slot. URL contract per P4-D1: only
 * `listIds`/`tab`/`page` are deep-linkable and mirrored back with `replace`.
 */
export const Feed: FunctionComponent = () => {
	const { t } = useTranslation();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();

	const {
		selectedLists,
		tab,
		page,
		sort,
		search,
		priorityFilterStatus,
		signalFilterStatus,
		filter,
		cards,
		meta,
		counts,
		companies,
		panelContextView,
		selectedCardId,
		jumpTargetCardId,
	} = useAppSelector(feedSelector);

	const [isHydrated, setIsHydrated] = useState(false);
	// Captured once: hydration reads the URL of the initial navigation only.
	const initialListsRef = useRef(parseFeedListIdsParam(searchParams.get("listIds")));
	const needsDefaultLists = initialListsRef.current.length === 0;

	const { data: defaultTrackerLists, isLoading: isLoadingDefaultLists } = useGetLeanListsQuery(
		{ page: 1, limit: DEFAULT_TRACKER_LISTS_LIMIT, sourceType: ListSourceType.TRACKER },
		{ skip: !needsDefaultLists }
	);

	useEffect(() => {
		dispatch(
			hydrateFeedFromUrl({
				selectedLists: initialListsRef.current,
				tab: searchParams.get("tab") ?? undefined,
				page: parseFeedPageParam(searchParams.get("page")),
			})
		);

		if (!needsDefaultLists) {
			setIsHydrated(true);
		}
	}, []);

	// Nav entry without listIds ⇒ select all tracker lists.
	useEffect(() => {
		if (!needsDefaultLists || !defaultTrackerLists) {
			return;
		}

		dispatch(
			hydrateFeedFromUrl({
				selectedLists: defaultTrackerLists.data.map((list) => ({
					id: list.id,
					title: list.name,
				})),
				tab: searchParams.get("tab") ?? undefined,
				page: parseFeedPageParam(searchParams.get("page")),
			})
		);
		setIsHydrated(true);
	}, [defaultTrackerLists, needsDefaultLists, dispatch]);

	// Slice → URL mirror (replace, not push — D1).
	useEffect(() => {
		if (!isHydrated) {
			return;
		}

		setSearchParams(
			(previous) => {
				const params = new URLSearchParams(previous);

				if (selectedLists.length > 0) {
					params.set("listIds", JSON.stringify(selectedLists));
				} else {
					params.delete("listIds");
				}

				params.set("tab", tab);
				params.set("page", String(page));

				return params;
			},
			{ replace: true }
		);
	}, [isHydrated, selectedLists, tab, page, setSearchParams]);

	const listIds = useMemo(() => selectedLists.map((list) => Number(list.id)), [selectedLists]);

	const filterArgs = useMemo(
		() => ({ listIds, search, priorityFilterStatus, signalFilterStatus, filter }),
		[listIds, search, priorityFilterStatus, signalFilterStatus, filter]
	);

	const hasSelection = isHydrated && listIds.length > 0;

	// `refetchOnMountOrArgChange` — the cards live in the slice, which only
	// updates on a fulfilled fetch. A cached page served without a refetch
	// (e.g. paging back to an already-visited page) would leave the slice
	// stuck on the previous page's cards.
	const { isFetching: isFeedFetching } = useGetFeedQuery(
		{ ...filterArgs, tab, page, sort },
		{ skip: !hasSelection, refetchOnMountOrArgChange: true }
	);

	useGetFeedCountsQuery(filterArgs, { skip: !hasSelection });

	// Add-lead answers and in-flight guards live here, not in the card: a Feed refetch swaps
	// every card for a spinner, and the answer to an add must outlive that (PREC-5041 S1/S3).
	const pendingAdds = useFeedPendingAdds({ cards, selectedListIds: listIds, isFeedFetching });

	// Keep a card always selected (no deselect state): after a real fetch settles
	// (ignoring stale slice cards on remount), if there is no *valid* selection —
	// nothing selected, or the selected card is not on the current page (a page /
	// tab / filter change moved it off) — select the first card. This makes the
	// first card auto-select on the initial load and re-select on every page
	// change, and guarantees the docked panel always has a target.
	const hasFetchedRef = useRef(false);

	useEffect(() => {
		if (isFeedFetching) {
			hasFetchedRef.current = true;

			return;
		}

		if (!hasFetchedRef.current || cards.length === 0) {
			return;
		}

		// A pending jump owns the selection — it runs after this effect and would otherwise
		// have cards[0] selected out from under it for a frame (D3).
		if (jumpTargetCardId !== null) {
			return;
		}

		const hasValidSelection =
			selectedCardId !== null && cards.some((card) => feedCardKey(card) === selectedCardId);

		if (!hasValidSelection) {
			dispatch(setFeedSelectedCardId(feedCardKey(cards[0])));
		}
	}, [isFeedFetching, cards, selectedCardId, jumpTargetCardId, dispatch]);

	// Jump-to-company (D3): selects, scrolls to and flashes the picked company's card once
	// its page has landed.
	const { headerRef, registerCardRef, flashCardId } = useFeedJumpToCompany({
		isFetching: isFeedFetching,
	});

	// Docked lead panel (D6) + ignore-this-time with undo (D3).
	const { panelChatListPersonId, openForJob } = useFeedDockedPanel();

	// Whole-panel minify (host-managed, mirroring the extension's `panelHidden`):
	// the collapse grabber shrinks the panel to a slim right-edge strip instead of
	// closing it — the card selection stays, so re-expanding shows the same lead.
	// A pure view toggle: card clicks / auto-select keep updating the selection
	// underneath while collapsed, like the Ready-to-send lead column.
	const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
	const { ignoreRun, undoState, undoIgnore, isUndoing, ignoreError } = useFeedRunIgnore();

	// Footer (…) overflow-menu actions — lead-status changes + tracker removals (page
	// owns their mutations + toasts so the toast survives the card's unmount on refetch).
	const { setStatus, toast: leadStatusToast } = useFeedLeadStatus();
	const { removePerson, removeCompany, toast: removalToast } = useFeedTrackerRemoval();

	// One-shot "AI drafts are ready" toast when a generation finishes (P6).
	const draftsReadyVisible = useFeedDraftsReadyToast();

	// AI generation plumbing (P6 D8): hydrate the job map from GET /active and
	// keep it live via the company-room socket — both at the page root so they
	// survive tab switches (the useFeedDraftGeneration lesson, plan §6.4).
	useGetActiveGenerationsQuery();
	useCompanyGenerationsSocket();
	const generationJobs = useAppSelector(selectGenerationJobs);
	const { consumeJob } = useFeedReviewNow();
	const { jumpToJobCard } = useFeedQueueJump({ companiesArgs: { ...filterArgs, tab, sort } });

	/**
	 * Queue-pill pick (D6, PREC-3712): go to the lead's card — paging to it if it is elsewhere in
	 * the result set — and let the card selection point the panel at the lead. A ready job is
	 * consumed on the way and lands on Chat. Only a lead with no card in this tab/filter set
	 * falls back to opening the panel from the job's own display fields.
	 */
	const handleQueueSelectJob = async (job: GenerationJob) => {
		const isReady = isGenerationJobReady(job);

		// Before the jump, not after: the lookup can await a fetch, and the ready chip should
		// clear on the click, not once the card lands.
		if (isReady) {
			consumeJob(job);
		}

		const hasCard = await jumpToJobCard(job, isReady ? "chat" : undefined);

		if (!hasCard) {
			openForJob(job);

			if (isReady) {
				dispatch(setFeedPanelContextView("chat"));
			}
		}
	};

	// The jump dropdown's data — fetched lazily from its first open onward so
	// sort/filter changes keep the open panel fresh without paying for it before.
	const [isJumpEnabled, setIsJumpEnabled] = useState(false);
	const { isFetching: isCompaniesFetching } = useGetFeedCompaniesQuery(
		{ ...filterArgs, tab, sort },
		{ skip: !hasSelection || !isJumpEnabled }
	);

	const tabs = useMemo<FeedTabItem[]>(
		() => [
			{ value: "new", label: t("feed.tabs.new"), count: counts?.new },
			{ value: "review", label: t("feed.tabs.review"), count: counts?.review },
			{ value: "assigned", label: t("feed.tabs.assigned"), count: counts?.assigned },
			{ value: "ignored", label: t("feed.tabs.ignored"), count: counts?.ignored },
			{
				value: FEED_TRACKED_TAB,
				label: t("feed.tabs.trackedProfiles"),
				count: counts?.trackedProfiles,
				divider: true,
			},
		],
		[t, counts]
	);

	const singleList = selectedLists.length === 1 ? selectedLists[0] : null;
	const hasNoTrackerLists = isHydrated && selectedLists.length === 0;

	if (!isHydrated && (isLoadingDefaultLists || needsDefaultLists)) {
		return (
			<div className="flex items-center justify-center w-full h-[50vh]">
				<Spinner color="#4d66f5" />
			</div>
		);
	}

	const renderCards = () => {
		if (isFeedFetching) {
			return (
				<div className="flex items-center justify-center py-16">
					<Spinner color="#4d66f5" />
				</div>
			);
		}

		if (cards.length === 0) {
			return <FeedEmptyState title={t("feed.emptyStates.noCompanies")} />;
		}

		return (
			<>
				{/* The docked lead panel is a 450px fixed right-edge overlay (ContactLeadPanel).
				    Reserve that width + a ~24px gap so the cards fill the remaining width and the
				    layout stays stable while it's open; when minified to the slim strip, reclaim
				    the freed width (only the ~35px strip stays reserved) so the cards reflow. */}
				<div
					className={classNames(
						"flex flex-col gap-3 px-4 py-3",
						isPanelCollapsed ? "max-w-[calc(100%-58px)]" : "max-w-[calc(100%-474px)]"
					)}
				>
					{cards.map((card) => (
						<RunCard
							key={feedCardKey(card)}
							card={card}
							cardRef={registerCardRef(feedCardKey(card))}
							isFeedFetching={isFeedFetching}
							isFlashing={flashCardId === feedCardKey(card)}
							onChangeStatus={setStatus}
							onIgnore={ignoreRun}
							onRemoveCompany={removeCompany}
							onRemovePerson={removePerson}
						/>
					))}
				</div>
				<FeedPager
					page={page}
					pageCount={meta?.pageCount ?? 1}
					onPageChange={(nextPage) => dispatch(setFeedPage(nextPage))}
				/>
			</>
		);
	};

	return (
		<div className="-ml-4 -mt-3 w-[calc(100%_+_2rem)] flex flex-col items-stretch">
			{/* The whole top nav (topbar + tabs + filter bar) stays pinned to the top of the
			    outlet scroll container while the run cards scroll underneath (PREC-3702).
			    -top-3: sticky insets resolve against the scroll container's content box, so a
			    plain top-0 would pin 12px down (the outlet's pt-3) and let cards bleed through
			    that strip — the -top-3 cancels it, like the root's -mt-3 does for the layout.
			    bg-white so cards can't show through the pinned bars. */}
			<div ref={headerRef} className="sticky -top-3 z-30 bg-white" data-testid="feed-sticky-header">
				<FeedTopbar
					isPanelCollapsed={isPanelCollapsed}
					selectedLists={selectedLists}
					onChangeSelectedLists={(lists) => dispatch(setFeedSelectedLists(lists))}
				/>

				{!hasNoTrackerLists && (
					<>
						<FeedTabs
							currentTab={tab}
							jumpSlot={
								<JumpToCompanyDropdown
									companies={companies}
									isLoading={isCompaniesFetching}
									sort={sort}
									onJump={(entry) =>
										dispatch(
											jumpToFeedCompany({
												page: entry.page,
												cardId: feedIdentity(entry.runId, entry.trackerOrganizationId),
											})
										)
									}
									onOpenChange={(open) => open && setIsJumpEnabled(true)}
									onSortChange={(nextSort) => dispatch(setFeedSort(nextSort))}
								/>
							}
							tabs={tabs}
							onTabChange={(nextTab) => dispatch(setFeedTab(nextTab))}
						/>

						<FeedFilterBar
							aiQueueSlot={<AiQueuePill jobs={generationJobs} onSelectJob={handleQueueSelectJob} />}
							filter={filter}
							priorityFilterStatus={priorityFilterStatus}
							searchValue={search}
							signalFilterStatus={signalFilterStatus}
							singleList={singleList}
							onFilterChange={(value) => dispatch(setFeedFilter(value))}
							onPriorityFilterChange={(value) => dispatch(setFeedPriorityFilterStatus(value))}
							onSearchChange={(value) => dispatch(setFeedSearch(value))}
							onSignalFilterChange={(value) => dispatch(setFeedSignalFilterStatus(value))}
						/>
					</>
				)}
			</div>

			{hasNoTrackerLists ? (
				<FeedEmptyState
					action={
						<Button
							size={ButtonSize.S}
							title={t("feed.emptyStates.noTrackerListsCta")}
							onClick={() => navigate(ROUTE_CONFIG.PROFILE_TRACKER)}
						/>
					}
					description={t("feed.emptyStates.noTrackerListsDescription")}
					title={t("feed.emptyStates.noTrackerListsTitle")}
				/>
			) : (
				// isolate: card content carries high z-indexes (Tooltip triggers are z-100)
				// that would otherwise paint over the z-30 sticky nav while scrolling under
				// it. Safe because every floating card piece (tooltips, strength/generate/
				// person-switch/reach-out menus) portals to document.body.
				<div className="isolate flex w-full">
					<div className="flex-1 min-w-0">
						<FeedPendingAddsContext.Provider value={pendingAdds}>
							{renderCards()}
						</FeedPendingAddsContext.Provider>
					</div>
					{/* Docked lead panel layout reservation (P4 D8); the panel itself is
					    the fixed overlay below, as on Contacts / Global Search (D6). */}
					<aside className="w-0 shrink-0" data-testid="feed-docked-panel-slot" />
				</div>
			)}

			{isPanelCollapsed ? (
				// Minified: a slim right-edge strip carrying the grabber to re-expand. The
				// panel selection is untouched, so expanding restores the same lead. The
				// grabber's parent must be positioned and must not clip overflow-x so the
				// pill's outer half can overhang the left edge.
				<div
					className="fixed right-0 top-0 z-[150] h-full w-[35px] border-l border-border-light bg-white shadow-lg"
					data-testid="feed-docked-panel-collapsed"
				>
					<CollapseGrabber
						ariaLabel={t("basics.expand")}
						collapsed
						edge="left"
						onToggle={() => setIsPanelCollapsed(false)}
					/>
				</div>
			) : (
				<ContactLeadPanel
					chatListPersonId={panelChatListPersonId}
					chatPending={!!panelChatListPersonId}
					closeOnOutsideClick={false}
					initialContextView={panelContextView}
					onCollapse={() => setIsPanelCollapsed(true)}
				/>
			)}

			{undoState && (
				<FeedUndoToast
					isBusy={isUndoing}
					message={t("feed.runCard.ignoredToast", { company: undoState.companyName ?? "" })}
					onUndo={undoIgnore}
				/>
			)}
			{ignoreError && !undoState && <FeedUndoToast message={ignoreError} variant="error" />}

			{leadStatusToast && (
				<FeedUndoToast message={leadStatusToast.message} variant={leadStatusToast.variant} />
			)}
			{removalToast && (
				<FeedUndoToast message={removalToast.message} variant={removalToast.variant} />
			)}
			{draftsReadyVisible && <FeedUndoToast message={t("feed.aiQueue.draftsReadyToast")} />}
		</div>
	);
};
